import { useState, useEffect, useMemo } from "react";

/**
 * Feedback Dashboard - Admin view for managing user feedback
 * Restricted to mark.herman@3dcolor.com only
 */
export default function FeedbackDashboard({ onBackToApp, userEmail }) {
  const [feedback, setFeedback] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  // Selected feedback for detail view
  const [selectedFeedback, setSelectedFeedback] = useState(null);

  // Resolution form
  const [resolution, setResolution] = useState("");
  const [updating, setUpdating] = useState(false);

  // Load feedback and stats
  useEffect(() => {
    loadFeedback();
    loadStats();
  }, []);

  const loadFeedback = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = sessionStorage.getItem("msal.backendToken");
      const res = await fetch("/api/feedback?limit=500", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error("Failed to load feedback");

      const data = await res.json();
      setFeedback(data.feedback || []);
    } catch (err) {
      console.error("Error loading feedback:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const token = sessionStorage.getItem("msal.backendToken");
      const res = await fetch("/api/feedback/stats", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const data = await res.json();
        setStats(data.stats);
      }
    } catch (err) {
      console.error("Error loading stats:", err);
    }
  };

  // Filter feedback
  const filteredFeedback = useMemo(() => {
    return feedback.filter((item) => {
      if (statusFilter !== "all" && item.status !== statusFilter) return false;
      if (typeFilter !== "all" && item.type !== typeFilter) return false;
      return true;
    });
  }, [feedback, statusFilter, typeFilter]);

  // Update feedback status
  const updateStatus = async (id, newStatus) => {
    setUpdating(true);
    try {
      const token = sessionStorage.getItem("msal.backendToken");
      const res = await fetch(`/api/feedback/${id}`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          status: newStatus,
          resolution: newStatus === "resolved" ? resolution : undefined,
          resolvedBy: newStatus === "resolved" ? userEmail : undefined,
        }),
      });

      if (!res.ok) throw new Error("Failed to update");

      // Refresh data
      await loadFeedback();
      await loadStats();

      // Update selected if it was the one we updated
      if (selectedFeedback?.id === id) {
        setSelectedFeedback((prev) => ({ ...prev, status: newStatus }));
      }

      setResolution("");
    } catch (err) {
      console.error("Error updating feedback:", err);
      alert("Failed to update feedback");
    } finally {
      setUpdating(false);
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case "new":
        return "bg-blue-500";
      case "reviewed":
        return "bg-yellow-500";
      case "resolved":
        return "bg-green-500";
      default:
        return "bg-slate-200";
    }
  };

  // Get type badge color
  const getTypeColor = (type) => {
    switch (type) {
      case "bug":
        return "bg-red-500";
      case "suggestion":
        return "bg-purple-500";
      case "question":
        return "bg-blue-400";
      default:
        return "bg-slate-200";
    }
  };

  return (
    <div className="flex-1 overflow-auto bg-slate-100 text-gray-900 flex flex-col">

      {/* STATS BAR */}
      {stats && (
        <div className="bg-white border-b border-gray-200 px-4 py-3">
          <div className="max-w-7xl mx-auto flex items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-gray-500">Total:</span>
              <span className="font-bold">{stats.total}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
              <span className="text-gray-500">New:</span>
              <span className="font-bold text-blue-400">{stats.new}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
              <span className="text-gray-500">Reviewed:</span>
              <span className="font-bold text-yellow-400">{stats.reviewed}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              <span className="text-gray-500">Resolved:</span>
              <span className="font-bold text-green-400">{stats.resolved}</span>
            </div>
            <div className="border-l border-gray-300 pl-6 flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                <span className="text-gray-500">Bugs:</span>
                <span className="font-bold">{stats.bugs}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                <span className="text-gray-500">Suggestions:</span>
                <span className="font-bold">{stats.suggestions}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-blue-400 rounded-full"></span>
                <span className="text-gray-500">Questions:</span>
                <span className="font-bold">{stats.questions}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FILTERS */}
      <div className="bg-slate-100 border-b border-gray-200 px-4 py-2">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <span className="text-gray-500 text-sm">Filter:</span>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-gray-700 border border-gray-300 rounded px-2 py-1 text-sm"
          >
            <option value="all">All Status</option>
            <option value="new">New</option>
            <option value="reviewed">Reviewed</option>
            <option value="resolved">Resolved</option>
          </select>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="bg-gray-700 border border-gray-300 rounded px-2 py-1 text-sm"
          >
            <option value="all">All Types</option>
            <option value="bug">Bugs</option>
            <option value="suggestion">Suggestions</option>
            <option value="question">Questions</option>
          </select>

          <span className="text-gray-500 text-sm ml-auto">
            Showing {filteredFeedback.length} of {feedback.length}
          </span>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="flex-1 flex">
        {/* FEEDBACK LIST */}
        <div className="flex-1 overflow-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">Loading...</div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-red-400">{error}</div>
            </div>
          ) : filteredFeedback.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">No feedback found</div>
            </div>
          ) : (
            <div className="max-w-4xl mx-auto space-y-3">
              {filteredFeedback.map((item) => (
                <div
                  key={item.id}
                  onClick={() => setSelectedFeedback(item)}
                  className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                    selectedFeedback?.id === item.id
                      ? "bg-gray-700 border-purple-500"
                      : "bg-slate-100 border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      {/* Badges */}
                      <div className="flex items-center gap-2 mb-2">
                        <span
                          className={`px-2 py-0.5 rounded text-xs font-medium ${getTypeColor(
                            item.type
                          )}`}
                        >
                          {item.type}
                        </span>
                        <span
                          className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(
                            item.status
                          )}`}
                        >
                          {item.status}
                        </span>
                        {item.workCenter && (
                          <span className="px-2 py-0.5 bg-gray-600 rounded text-xs">
                            {item.workCenter}
                          </span>
                        )}
                      </div>

                      {/* Message */}
                      <p className="text-gray-900 line-clamp-2">{item.message}</p>

                      {/* Meta */}
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <span>{item.employeeName || "Anonymous"}</span>
                        <span>{formatDate(item.createdAt)}</span>
                        {item.page && <span>Page: {item.page}</span>}
                      </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="flex flex-col gap-1">
                      {item.status === "new" && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            updateStatus(item.id, "reviewed");
                          }}
                          className="px-2 py-1 bg-yellow-600 hover:bg-yellow-700 rounded text-xs"
                          disabled={updating}
                        >
                          Mark Reviewed
                        </button>
                      )}
                      {item.status !== "resolved" && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedFeedback(item);
                          }}
                          className="px-2 py-1 bg-green-600 hover:bg-green-700 rounded text-xs"
                        >
                          Resolve
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* DETAIL PANEL */}
        {selectedFeedback && (
          <div className="w-96 bg-white border-l border-gray-200 p-4 overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Feedback #{selectedFeedback.id}</h3>
              <button
                onClick={() => setSelectedFeedback(null)}
                className="p-1 hover:bg-slate-100 rounded"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Badges */}
            <div className="flex items-center gap-2 mb-4">
              <span
                className={`px-2 py-0.5 rounded text-xs font-medium ${getTypeColor(
                  selectedFeedback.type
                )}`}
              >
                {selectedFeedback.type}
              </span>
              <span
                className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(
                  selectedFeedback.status
                )}`}
              >
                {selectedFeedback.status}
              </span>
            </div>

            {/* Details */}
            <div className="space-y-4">
              <div>
                <label className="text-xs text-gray-500 uppercase">Message</label>
                <p className="mt-1 text-gray-900 whitespace-pre-wrap">
                  {selectedFeedback.message}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 uppercase">From</label>
                  <p className="mt-1">{selectedFeedback.employeeName || "Anonymous"}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase">Work Center</label>
                  <p className="mt-1">{selectedFeedback.workCenter || "—"}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase">Page</label>
                  <p className="mt-1">{selectedFeedback.page || "—"}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase">Submitted</label>
                  <p className="mt-1">{formatDate(selectedFeedback.createdAt)}</p>
                </div>
              </div>

              {selectedFeedback.resolution && (
                <div>
                  <label className="text-xs text-gray-500 uppercase">Resolution</label>
                  <p className="mt-1 text-green-600 whitespace-pre-wrap">
                    {selectedFeedback.resolution}
                  </p>
                  {selectedFeedback.resolvedBy && (
                    <p className="mt-1 text-xs text-gray-500">
                      By {selectedFeedback.resolvedBy} on{" "}
                      {formatDate(selectedFeedback.resolvedAt)}
                    </p>
                  )}
                </div>
              )}

              {/* Actions */}
              {selectedFeedback.status !== "resolved" && (
                <div className="pt-4 border-t border-gray-200">
                  <label className="text-xs text-gray-500 uppercase">
                    Resolution Notes (optional)
                  </label>
                  <textarea
                    value={resolution}
                    onChange={(e) => setResolution(e.target.value)}
                    placeholder="Add resolution notes..."
                    className="w-full mt-1 bg-slate-100 border border-gray-300 rounded p-2 text-sm resize-none"
                    rows={3}
                  />

                  <div className="flex gap-2 mt-3">
                    {selectedFeedback.status === "new" && (
                      <button
                        onClick={() => updateStatus(selectedFeedback.id, "reviewed")}
                        className="flex-1 px-3 py-2 bg-yellow-600 hover:bg-yellow-700 rounded text-sm font-medium"
                        disabled={updating}
                      >
                        Mark Reviewed
                      </button>
                    )}
                    <button
                      onClick={() => updateStatus(selectedFeedback.id, "resolved")}
                      className="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 rounded text-sm font-medium"
                      disabled={updating}
                    >
                      {updating ? "Updating..." : "Resolve"}
                    </button>
                  </div>
                </div>
              )}

              {/* Reopen button for resolved items */}
              {selectedFeedback.status === "resolved" && (
                <div className="pt-4 border-t border-gray-200">
                  <button
                    onClick={() => updateStatus(selectedFeedback.id, "new")}
                    className="w-full px-3 py-2 bg-gray-600 hover:bg-slate-100 rounded text-sm font-medium"
                    disabled={updating}
                  >
                    Reopen
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
